import type {
	CoursesAdminListParticipantsQueryDto,
	CoursesAdminReorderQuestionsDto,
	CoursesAdminSetAiPredictionDto,
	CoursesAdminSetMasterPredictionDto,
	CoursesAdminSetOutcomeDto,
	CoursesAdminUpsertChallengeDto,
	CoursesAdminUpsertChallengeEmailSettingDto,
	CoursesAdminUpsertQuestionDto,
} from '@sportspredict/shared';
import type { Wire } from '../../types/common.types';
import { coursesApi } from './courses-base.api';
import { invalidatesAfterDelete } from '../../utils/courses-errors.util';
import type {
	AdminCheckStaffOutput,
	AdminExportParticipantsInput,
	AdminExportParticipantsOutput,
	AdminListChallengesOutput,
	CreateChallengeBannerUploadUrlInput,
	CreateChallengeBannerUploadUrlOutput,
	ConfirmChallengeBannerInput,
	ConfirmChallengeBannerOutput,
	AdminUpsertChallengeInput,
	AdminUpsertChallengeOutput,
	AdminDeleteChallengeInput,
	AdminDeleteChallengeOutput,
	AdminGetChallengeInput,
	AdminGetChallengeOutput,
	AdminListQuestionsInput,
	AdminListQuestionsOutput,
	AdminUpsertQuestionInput,
	AdminUpsertQuestionOutput,
	AdminDeleteQuestionInput,
	AdminDeleteQuestionOutput,
	AdminReorderQuestionsInput,
	AdminReorderQuestionsOutput,
	AdminSetMasterPredictionInput,
	AdminSetMasterPredictionOutput,
	AdminSetOutcomeInput,
	AdminSetOutcomeOutput,
	AdminSetAiPredictionInput,
	AdminSetAiPredictionOutput,
	AdminComputeAiAveragesInput,
	AdminComputeAiAveragesOutput,
	AdminListParticipantsInput,
	AdminListParticipantsOutput,
	AdminGetParticipantDetailInput,
	AdminGetParticipantDetailOutput,
	AdminListEmailSettingsInput,
	AdminListEmailSettingsOutput,
	AdminUpsertEmailSettingInput,
	AdminUpsertEmailSettingOutput,
} from '../../types/admin-challenges.types';

const TAG = 'courses:admin-challenges' as const;
const INVALIDATES = [TAG, 'courses:challenges', 'courses:courses', 'courses:dashboard'] as const;

export const adminChallengesEndpoints = coursesApi.injectEndpoints({
	endpoints: (builder) => ({
		adminCheckStaff: builder.query<AdminCheckStaffOutput, void>({
			query: () => ({ url: 'courses/admin/challenges/check-staff' }),
			providesTags: [TAG],
		}),
		adminListChallenges: builder.query<AdminListChallengesOutput, void>({
			query: () => ({ url: 'courses/admin/challenges' }),
			providesTags: [TAG],
		}),
		adminUpsertChallenge: builder.mutation<AdminUpsertChallengeOutput, AdminUpsertChallengeInput>({
			query: (body) => ({
				url: 'courses/admin/challenges',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminUpsertChallengeDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminDeleteChallenge: builder.mutation<AdminDeleteChallengeOutput, AdminDeleteChallengeInput>({
			query: ({ id }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: invalidatesAfterDelete(INVALIDATES),
		}),
		adminGetChallenge: builder.query<AdminGetChallengeOutput, AdminGetChallengeInput>({
			query: ({ challengeId }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}`,
			}),
			providesTags: [TAG],
		}),
		adminListQuestions: builder.query<AdminListQuestionsOutput, AdminListQuestionsInput>({
			query: ({ challengeId }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}/questions`,
			}),
			providesTags: [TAG],
		}),
		adminUpsertQuestion: builder.mutation<AdminUpsertQuestionOutput, AdminUpsertQuestionInput>({
			query: (body) => ({
				url: 'courses/admin/challenges/questions',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminUpsertQuestionDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminDeleteQuestion: builder.mutation<AdminDeleteQuestionOutput, AdminDeleteQuestionInput>({
			query: ({ id }) => ({
				url: `courses/admin/challenges/questions/${encodeURIComponent(id)}`,
				method: 'DELETE',
			}),
			invalidatesTags: invalidatesAfterDelete(INVALIDATES),
		}),
		adminReorderQuestions: builder.mutation<
			AdminReorderQuestionsOutput,
			AdminReorderQuestionsInput
		>({
			query: (body) => ({
				url: 'courses/admin/challenges/questions/reorder',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminReorderQuestionsDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminSetMasterPrediction: builder.mutation<
			AdminSetMasterPredictionOutput,
			AdminSetMasterPredictionInput
		>({
			query: (body) => ({
				url: 'courses/admin/challenges/master-predictions',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminSetMasterPredictionDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminSetOutcome: builder.mutation<AdminSetOutcomeOutput, AdminSetOutcomeInput>({
			query: (body) => ({
				url: 'courses/admin/challenges/outcomes',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminSetOutcomeDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminSetAiPrediction: builder.mutation<AdminSetAiPredictionOutput, AdminSetAiPredictionInput>({
			query: (body) => ({
				url: 'courses/admin/challenges/ai-predictions',
				method: 'POST',
				body: body satisfies Wire<CoursesAdminSetAiPredictionDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminComputeAiAverages: builder.mutation<
			AdminComputeAiAveragesOutput,
			AdminComputeAiAveragesInput
		>({
			query: ({ challengeId }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}/ai-averages`,
				method: 'POST',
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		adminListParticipants: builder.query<AdminListParticipantsOutput, AdminListParticipantsInput>({
			query: ({ challengeId, ...params }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}/participants`,
				params: params satisfies Wire<CoursesAdminListParticipantsQueryDto>,
			}),
			providesTags: [TAG],
		}),
		adminExportParticipants: builder.mutation<
			AdminExportParticipantsOutput,
			AdminExportParticipantsInput
		>({
			query: ({ challengeId }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}/participants/export`,
				method: 'POST',
			}),
		}),
		adminGetParticipantDetail: builder.query<
			AdminGetParticipantDetailOutput,
			AdminGetParticipantDetailInput
		>({
			query: ({ challengeId, studentId }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}/participants/${encodeURIComponent(studentId)}`,
			}),
			providesTags: [TAG],
		}),
		adminListEmailSettings: builder.query<
			AdminListEmailSettingsOutput,
			AdminListEmailSettingsInput
		>({
			query: ({ challengeId }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}/email-settings`,
			}),
			providesTags: [TAG],
		}),
		adminUpsertEmailSetting: builder.mutation<
			AdminUpsertEmailSettingOutput,
			AdminUpsertEmailSettingInput
		>({
			query: (body) => ({
				url: 'courses/admin/challenges/email-settings',
				method: 'PUT',
				body: body satisfies Wire<CoursesAdminUpsertChallengeEmailSettingDto>,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
		createChallengeBannerUploadUrl: builder.mutation<
			CreateChallengeBannerUploadUrlOutput,
			CreateChallengeBannerUploadUrlInput
		>({
			query: ({ challengeId, ...body }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}/banner/upload-url`,
				method: 'POST',
				body,
			}),
		}),
		confirmChallengeBanner: builder.mutation<
			ConfirmChallengeBannerOutput,
			ConfirmChallengeBannerInput
		>({
			query: ({ challengeId, ...body }) => ({
				url: `courses/admin/challenges/${encodeURIComponent(challengeId)}/banner/confirm`,
				method: 'POST',
				body,
			}),
			invalidatesTags: [...INVALIDATES],
		}),
	}),
});

export const {
	useAdminCheckStaffQuery,
	useLazyAdminCheckStaffQuery,
	useAdminListChallengesQuery,
	useLazyAdminListChallengesQuery,
	useCreateChallengeBannerUploadUrlMutation,
	useConfirmChallengeBannerMutation,
	useAdminUpsertChallengeMutation,
	useAdminDeleteChallengeMutation,
	useAdminGetChallengeQuery,
	useLazyAdminGetChallengeQuery,
	useAdminListQuestionsQuery,
	useLazyAdminListQuestionsQuery,
	useAdminUpsertQuestionMutation,
	useAdminDeleteQuestionMutation,
	useAdminReorderQuestionsMutation,
	useAdminSetMasterPredictionMutation,
	useAdminSetOutcomeMutation,
	useAdminSetAiPredictionMutation,
	useAdminComputeAiAveragesMutation,
	useAdminListParticipantsQuery,
	useLazyAdminListParticipantsQuery,
	useAdminExportParticipantsMutation,
	useAdminGetParticipantDetailQuery,
	useLazyAdminGetParticipantDetailQuery,
	useAdminListEmailSettingsQuery,
	useLazyAdminListEmailSettingsQuery,
	useAdminUpsertEmailSettingMutation,
} = adminChallengesEndpoints;
